import React, { useEffect, useRef, useState } from "react";
import { Compass, X } from "lucide-react";
import { consumeCarryingIntention } from "@/lib/carryingIntention";

/**
 * CarryingIntentionPanel — a TEMPORARY contextual section that appears
 * above the Journal composer when the member arrives via "Reflect in
 * Journal →" from TODAY'S INTENTION on the Daily Intentions tab.
 *
 * Mirrors `CarryingAffirmationPanel` architecturally but tells the right
 * story for an intention rather than an affirmation.
 *
 * Behaviour:
 *   • Reads the carrying intention once on mount via
 *     `consumeCarryingIntention()` (which also clears the key), so a
 *     plain visit to Journal never renders this panel.
 *   • Shows "TODAY I'M LIVING WITH" + the intention + the optional
 *     reflection prompt.
 *   • Can be dismissed with ×. Does not pre-fill the composer.
 *   • Does not persist. On reload or next Journal visit the panel is gone.
 */
export default function CarryingIntentionPanel() {
  const [carrying, setCarrying] = useState(null);
  // StrictMode double-invokes effects in dev; without a guard the second
  // run would consume a now-empty key and reset `carrying` back to null.
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    consumedRef.current = true;
    setCarrying(consumeCarryingIntention());
  }, []);

  if (!carrying) return null;

  return (
    <section
      className="relative overflow-hidden rounded-md border hairline bg-gradient-to-br from-[hsl(var(--sand))]/50 via-[hsl(var(--paper))] to-[hsl(var(--paper))] p-6 md:p-8 reveal"
      data-testid="journal-carrying-intention"
      aria-live="polite"
    >
      <button
        type="button"
        onClick={() => setCarrying(null)}
        data-testid="journal-carrying-intention-dismiss"
        aria-label="Dismiss"
        className="absolute top-3 right-3 w-7 h-7 grid place-items-center rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] hover:bg-[hsl(var(--sand))]/60 tab-link"
      >
        <X size={13} />
      </button>

      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--terracotta))]">
        <Compass size={11} />
        <span data-testid="journal-carrying-intention-eyebrow">Today I&apos;m living with</span>
        {carrying.category && (
          <span className="normal-case tracking-normal text-[hsl(var(--muted-foreground))]">
            · {carrying.category}
          </span>
        )}
      </div>

      <p
        className="mt-4 font-display italic text-xl md:text-2xl leading-snug text-[hsl(var(--fg))]"
        data-testid="journal-carrying-intention-text"
      >
        &ldquo;{carrying.text}&rdquo;
      </p>

      <p
        className="mt-4 text-[13px] text-[hsl(var(--muted-foreground))] italic"
        data-testid="journal-carrying-intention-prompt"
      >
        What would living this intention look like today?
      </p>
    </section>
  );
}
